/**
 * Owns the dashboard's Customize Controls state: which cards are on the grid,
 * which sit in the More Controls / Quick Actions tray, and the draft the user
 * is editing before they hit Done.
 *
 * Edits run against a draft copy so Cancel is a real undo — nothing is written
 * anywhere until Done.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DASHBOARD_VISIBLE_CONTROL_COUNT,
  DEFAULT_VISIBLE_CONTROL_IDS,
  isSameControlLayout,
  normalizeDashboardControlLayout,
  splitDashboardControls,
  toDashboardControls,
} from "./dashboardControlsCatalog";
import {
  fetchDashboardControlLayout,
  resetDashboardControlLayout,
  saveDashboardControlLayout,
  seedDashboardControlLayout,
} from "./dashboardControlsPreferences";

export default function useDashboardControls() {
  const [savedIds, setSavedIds] = useState(
    () => normalizeDashboardControlLayout(null).visibleIds
  );
  const [draftIds, setDraftIds] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadLayout = useCallback(async () => {
    const { visibleIds, source } = await fetchDashboardControlLayout();
    if (!isMountedRef.current) return;
    const layout = normalizeDashboardControlLayout(visibleIds);
    setSavedIds(layout.visibleIds);

    // The backend has nothing stored for this user and tree yet, so write the
    // layout we just rendered — defaults included — rather than leaving the
    // two out of sync until the user happens to open the editor.
    if (source !== "api") {
      seedDashboardControlLayout(layout.visibleIds, layout.hiddenIds);
    }
  }, []);

  useEffect(() => {
    loadLayout();
  }, [loadLayout]);

  // Switching family tree switches whose layout applies, the same signal the
  // dashboard already listens to for its data.
  useEffect(() => {
    const onContextChange = () => loadLayout();
    window.addEventListener("localStorageChange", onContextChange);
    return () =>
      window.removeEventListener("localStorageChange", onContextChange);
  }, [loadLayout]);

  const isEditing = draftIds !== null;
  const activeIds = isEditing ? draftIds : savedIds;

  // Split, not normalize: a draft is allowed to sit at 7 while the user picks
  // what to put in the freed slot. Only saving insists on the full 8.
  const { visibleIds, hiddenIds } = useMemo(
    () => splitDashboardControls(activeIds),
    [activeIds]
  );

  const visibleControls = useMemo(
    () => toDashboardControls(visibleIds),
    [visibleIds]
  );
  // The tray reads as a list to pick from rather than a layout, so it goes
  // A–Z by label — catalog order only tells you where a card used to sit on
  // the grid. Sorted here and not in splitDashboardControls, which hands the
  // same hidden ids to normalizeDashboardControlLayout to top up a short grid;
  // that fill should keep following the catalog.
  const hiddenControls = useMemo(
    () =>
      toDashboardControls(hiddenIds).sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
      ),
    [hiddenIds]
  );

  const startEditing = useCallback(() => {
    setDraftIds((current) => (current === null ? [...savedIds] : current));
  }, [savedIds]);

  const cancelEditing = useCallback(() => {
    setDraftIds(null);
  }, []);

  const addControl = useCallback((id) => {
    setDraftIds((current) => {
      if (current === null || current.includes(id)) return current;
      // The grid is a fixed 8: something has to come off before this goes on.
      if (current.length >= DASHBOARD_VISIBLE_CONTROL_COUNT) return current;
      return [...current, id];
    });
  }, []);

  const removeControl = useCallback((id) => {
    setDraftIds((current) =>
      current === null ? current : current.filter((item) => item !== id)
    );
  }, []);

  /** Drop `id` at the position `targetId` currently holds (iOS-style shift). */
  const moveControlTo = useCallback((id, targetId) => {
    setDraftIds((current) => {
      if (current === null || id === targetId) return current;
      const from = current.indexOf(id);
      const to = current.indexOf(targetId);
      if (from === -1 || to === -1) return current;
      const next = [...current];
      next.splice(from, 1);
      next.splice(to, 0, id);
      return next;
    });
  }, []);

  const resetDraftToDefault = useCallback(() => {
    setDraftIds((current) =>
      current === null ? current : [...DEFAULT_VISIBLE_CONTROL_IDS]
    );
  }, []);

  const saveEditing = useCallback(async () => {
    if (draftIds === null) return null;
    // Guard as well as disable the button: a half-filled grid never persists.
    if (draftIds.length !== DASHBOARD_VISIBLE_CONTROL_COUNT) return null;
    const next = [...draftIds];

    // Nothing changed — close without a round trip.
    if (isSameControlLayout(next, savedIds)) {
      setDraftIds(null);
      return { source: "unchanged" };
    }

    setIsSaving(true);
    // The leftovers ride along so the saved quick-actions tray matches what
    // the dashboard is about to render, and the layout being replaced goes
    // with them so only the cards that actually moved are posted.
    const nextHiddenIds = splitDashboardControls(next).hiddenIds;
    const previousLayout = splitDashboardControls(savedIds);
    const isDefault = isSameControlLayout(next, DEFAULT_VISIBLE_CONTROL_IDS);
    const result = isDefault
      ? await resetDashboardControlLayout(next, nextHiddenIds, previousLayout)
      : await saveDashboardControlLayout(next, nextHiddenIds, previousLayout);

    if (isMountedRef.current) {
      setSavedIds(next);
      setDraftIds(null);
      setIsSaving(false);
    }
    return result;
  }, [draftIds, savedIds]);

  return {
    visibleControls,
    hiddenControls,
    isEditing,
    isSaving,
    // How many slots the grid must hold, and whether the draft holds them.
    requiredControlCount: DASHBOARD_VISIBLE_CONTROL_COUNT,
    isGridFull: visibleIds.length >= DASHBOARD_VISIBLE_CONTROL_COUNT,
    canSave: visibleIds.length === DASHBOARD_VISIBLE_CONTROL_COUNT,
    startEditing,
    cancelEditing,
    saveEditing,
    addControl,
    removeControl,
    moveControlTo,
    resetDraftToDefault,
  };
}
